import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  CaretLeft,
  Check,
  CheckCircle,
  CloudArrowUp,
  Image as ImageIcon,
  InstagramLogo,
  SoundcloudLogo,
  YoutubeLogo,
  SpotifyLogo,
  Globe,
  Plus,
  X,
  SealCheck,
} from "@phosphor-icons/react";
import { disciplines as mockDisciplines, profileTypes } from "@/data/mockData";
import { useBrand } from "@/context/BrandContext";

const stages = [
  { key: "type", label: "Perfil" },
  { key: "identity", label: "Identidad" },
  { key: "story", label: "Historia" },
  { key: "links", label: "Enlaces" },
  { key: "goals", label: "Intenciones" },
  { key: "portfolio", label: "Portfolio" },
  { key: "review", label: "Revisar" },
];

const levels = [
  { key: "emerging", label: "Emergente", desc: "Menos de 3 años de práctica pública" },
  { key: "early", label: "Inicio de carrera", desc: "3-7 años · primeras exposiciones, releases, shows" },
  { key: "mid", label: "Mid-career", desc: "7-15 años · trayectoria consolidada" },
  { key: "established", label: "Consolidade", desc: "15+ años · obra reconocida" },
];

const interests = [
  "EXPOSICIONES",
  "RESIDENCIAS",
  "COMISIONES PAGADAS",
  "COLABS",
  "REPRESENTACIÓN GALERÍA",
  "SELLO DISCOGRÁFICO",
  "FEATURES EDITORIALES",
  "VENDER OBRA",
  "BOOKING EN VIVO",
  "WORKSHOPS",
  "RED DE COMUNIDAD",
  "MENTORES",
];

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { disciplines: liveDisciplines } = useBrand();
  const disciplines = liveDisciplines && liveDisciplines.length ? liveDisciplines : mockDisciplines;
  const [stepIdx, setStepIdx] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [createdHandle, setCreatedHandle] = useState(null);
  const stage = stages[stepIdx];

  const [form, setForm] = useState({
    type: "visual-artist",
    primaryDiscipline: "",
    subDisciplines: [],
    handle: "",
    name: "",
    email: "",
    password: "",
    city: "",
    country: "ES",
    languages: ["ES"],
    photo: null,
    cover: null,
    shortBio: "",
    longBio: "",
    links: {
      website: "",
      instagram: "",
      spotify: "",
      soundcloud: "",
      youtube: "",
    },
    level: "early",
    interests: [],
    works: [],
    acceptTerms: false,
    newsletter: true,
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleList = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value)
        ? f[key].filter((v) => v !== value)
        : [...f[key], value],
    }));

  const canAdvance = () => {
    if (stage.key === "identity")
      return form.handle && form.name && form.email && form.password.length >= 6;
    if (stage.key === "review") return form.acceptTerms;
    return true;
  };

  const pct = Math.round(((stepIdx + 1) / stages.length) * 100);

  return (
    <div
      data-testid="register-page"
      className="min-h-screen bg-[#050505] text-[#F4F4F5] flex flex-col"
    >
      {publishing ? (
        <PublishingOverlay
          form={form}
          error={error}
          onSubmit={async () => {
            try {
              const payload = {
                email: form.email,
                password: form.password,
                handle: form.handle || "artista_" + Date.now(),
                name: form.name,
                type: form.type,
                primary_discipline: form.primaryDiscipline,
                sub_disciplines: form.subDisciplines,
                city: form.city,
                country: form.country,
                languages: form.languages,
                short_bio: form.shortBio,
                long_bio: form.longBio,
                links: form.links,
                level: form.level,
                interests: form.interests,
                photo: form.photo,
                cover: form.cover,
                works: form.works,
              };
              const { data } = await api.post("/auth/register", payload);
              if (data.access_token)
                localStorage.setItem("elarte_token", data.access_token);
              setUser(data.user);
              setCreatedHandle(data.user.handle);
              return true;
            } catch (e) {
              setError(formatApiError(e));
              return false;
            }
          }}
          onDone={() =>
            navigate(`/artista/${createdHandle || form.handle || "mar_quintero"}`)
          }
          onCancel={() => {
            setPublishing(false);
            setError("");
          }}
        />
      ) : null}
      {/* Masthead */}
      <header className="sticky top-0 z-40 bg-[#050505]/90 backdrop-blur-lg border-b border-[#27272A]">
        <div className="max-w-[1300px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="font-display text-xl font-black uppercase">
            ELARTE<span className="text-[#FF3E00]">/</span>NET
          </Link>
          <div className="hidden md:flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] text-[#A1A1AA]">
            {stages.map((s, i) => (
              <span
                key={s.key}
                className={`px-2 py-1 ${
                  i === stepIdx
                    ? "bg-[#FF3E00] text-white"
                    : i < stepIdx
                    ? "text-[#00FF66]"
                    : "text-[#525252]"
                }`}
              >
                {i < stepIdx ? "✓" : String(i + 1).padStart(2, "0")}·{s.label}
              </span>
            ))}
          </div>
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.22em]">
            {pct}%
          </div>
        </div>
        <div className="h-0.5 bg-[#27272A]">
          <div
            className="h-full bg-[#FF3E00] transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </header>

      <div className="flex-1 max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-10">
        {/* LEFT — editorial framing */}
        <aside className="hidden lg:flex flex-col justify-between sticky top-24 self-start h-[calc(100vh-140px)]">
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-4">
              REGISTRO DE ARTISTA · PASO {stepIdx + 1}/{stages.length}
            </div>
            <h1 className="font-display text-6xl xl:text-7xl font-black tracking-tighter uppercase leading-[0.9]">
              {stage.key === "type" && (
                <>Empieza por{" "}<span className="text-[#FF3E00]">definirte</span>.</>
              )}
              {stage.key === "identity" && (
                <>Tu <span className="text-[#FF3E00]">nombre</span><br/>en el directorio.</>
              )}
              {stage.key === "story" && (
                <>Cuéntanos <span className="text-[#FF3E00]">quién</span> eres.</>
              )}
              {stage.key === "links" && (
                <>Conecta tu <span className="text-[#FF3E00]">mundo</span>.</>
              )}
              {stage.key === "goals" && (
                <>¿Qué <span className="text-[#FF3E00]">buscas</span>?</>
              )}
              {stage.key === "portfolio" && (
                <>Tus primeras <span className="text-[#FF3E00]">obras</span>.</>
              )}
              {stage.key === "review" && (
                <>Listo para <span className="text-[#FF3E00]">lanzar</span>.</>
              )}
            </h1>
            <p className="font-mono text-sm text-[#A1A1AA] tracking-wider leading-relaxed mt-6 max-w-md">
              {stage.key === "type" && "// ¿QUÉ TIPO DE CREADORX ERES? ESTO DEFINE CÓMO TE MOSTRAREMOS."}
              {stage.key === "identity" && "// TU HANDLE SERÁ TU URL PÚBLICA: ELARTE.NET/ARTISTA/@TU_HANDLE"}
              {stage.key === "story" && "// DOS LÍNEAS PARA EL DIRECTORIO Y UN PÁRRAFO PARA TU KIT DE PRENSA."}
              {stage.key === "links" && "// AGREGA TUS PLATAFORMAS. NO PEDIMOS PASSWORDS · SÓLO LINKS PÚBLICOS."}
              {stage.key === "goals" && "// SABER QUÉ BUSCAS NOS AYUDA A RECOMENDARTE CONVOCATORIAS Y COLABS."}
              {stage.key === "portfolio" && "// SUBE 3-5 OBRAS PARA ACTIVAR TU PERFIL. PUEDES AGREGAR MÁS DESPUÉS."}
              {stage.key === "review" && "// TODO LO QUE COMPARTAS AHORA APARECERÁ EN TU PERFIL PÚBLICO."}
            </p>
          </div>

          <div className="border border-[#27272A] bg-[#0a0a0a] p-4 max-w-md">
            <div className="flex items-center gap-2 mb-2">
              <SealCheck size={13} weight="fill" className="text-[#00FF66]" />
              <span className="font-mono text-[10px] text-[#00FF66] tracking-[0.22em]">
                REGISTRO VERIFICADO
              </span>
            </div>
            <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider leading-relaxed">
              VERIFICAMOS TU IDENTIDAD MANUALMENTE EN 24-48H. SIN BOTS. SIN
              PORTFOLIOS FALSOS. SIN ESPAM.
            </p>
          </div>
        </aside>

        {/* RIGHT — form */}
        <main className="min-w-0">
          {/* Mobile heading */}
          <div className="lg:hidden mb-6">
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
              PASO {stepIdx + 1} / {stages.length} · {stage.label.toUpperCase()}
            </div>
            <h1 className="font-display text-4xl font-black tracking-tighter uppercase leading-[0.95]">
              {stage.label}
            </h1>
          </div>

          {/* STAGES */}
          {stage.key === "type" && (
            <div data-testid="stage-type" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {profileTypes.map((p) => (
                <button
                  key={p.slug}
                  data-testid={`type-${p.slug}`}
                  onClick={() => update({ type: p.slug })}
                  className={`relative text-left p-5 border transition-all ${
                    form.type === p.slug
                      ? "border-[#FF3E00] bg-[#FF3E00]/5"
                      : "border-[#27272A] bg-[#0a0a0a] hover:border-[#F4F4F5]"
                  }`}
                >
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-3">
                    {p.no}
                  </div>
                  <div className="font-display font-black text-2xl tracking-tight uppercase">
                    {p.title}
                  </div>
                  <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-2 leading-relaxed">
                    {p.desc}
                  </div>
                  {form.type === p.slug ? (
                    <div className="absolute top-3 right-3 bg-[#FF3E00] text-white h-6 w-6 inline-flex items-center justify-center">
                      <Check size={12} weight="bold" />
                    </div>
                  ) : null}
                </button>
              ))}
            </div>
          )}

          {stage.key === "identity" && (
            <div data-testid="stage-identity" className="space-y-5 max-w-xl">
              <Field
                label="@HANDLE"
                helper={`elarte.net/artista/${form.handle || "tu_handle"}`}
                value={form.handle}
                onChange={(v) =>
                  update({
                    handle: v
                      .toLowerCase()
                      .replace(/\s+/g, "_")
                      .replace(/[^a-z0-9_]/g, ""),
                  })
                }
                placeholder="tu_arte"
              />
              <Field
                label="NOMBRE ARTÍSTICO"
                value={form.name}
                onChange={(v) => update({ name: v })}
                placeholder="Mar Quintero"
              />
              <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-3">
                <Field
                  label="CIUDAD"
                  value={form.city}
                  onChange={(v) => update({ city: v })}
                  placeholder="Madrid"
                />
                <div>
                  <Label>PAÍS</Label>
                  <select
                    value={form.country}
                    onChange={(e) => update({ country: e.target.value })}
                    className="mt-2 w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-3 font-mono text-sm"
                  >
                    {["ES", "MX", "AR", "CO", "CL", "PE", "UY", "BR", "US", "DE", "FR", "IT", "JP"].map(
                      (c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>
              <Field
                label="EMAIL"
                type="email"
                value={form.email}
                onChange={(v) => update({ email: v })}
                placeholder="tu@correo.com"
              />
              <Field
                label="CONTRASEÑA · MIN 6"
                type="password"
                value={form.password}
                onChange={(v) => update({ password: v })}
                placeholder="••••••••"
              />
              <div>
                <Label>IDIOMAS QUE HABLAS</Label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["ES", "EN", "PT", "FR", "IT", "CAT", "JP"].map((l) => {
                    const active = form.languages.includes(l);
                    return (
                      <button
                        key={l}
                        onClick={() => toggleList("languages", l)}
                        className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-colors ${
                          active
                            ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                            : "border-[#27272A] text-[#A1A1AA] hover:text-white"
                        }`}
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {stage.key === "story" && (
            <div data-testid="stage-story" className="space-y-6 max-w-xl">
              <PhotoUpload
                label="FOTO DE PERFIL"
                value={form.photo}
                onChange={(v) => update({ photo: v })}
                aspect="aspect-square"
                sub="JPG · 800×800 min · Alta resolución para tu kit de prensa"
              />
              <PhotoUpload
                label="IMAGEN DE PORTADA"
                value={form.cover}
                onChange={(v) => update({ cover: v })}
                aspect="aspect-[3/1]"
                sub="Opcional · obra propia o foto de estudio"
              />

              <Field
                textarea
                rows={2}
                label="BIO CORTA · 140 CAR."
                helper={`${form.shortBio.length}/140`}
                value={form.shortBio}
                onChange={(v) => update({ shortBio: v.slice(0, 140) })}
                placeholder="Pintora contemporánea basada en Madrid. Explora la memoria como material pictórico."
              />
              <Field
                textarea
                rows={6}
                label="BIO LARGA · PARA KIT DE PRENSA"
                helper={`${form.longBio.length}/1500`}
                value={form.longBio}
                onChange={(v) => update({ longBio: v.slice(0, 1500) })}
                placeholder="Cuenta tu práctica, qué te mueve, desde dónde hablas. ~200-400 palabras funciona bien."
              />
            </div>
          )}

          {stage.key === "links" && (
            <div data-testid="stage-links" className="space-y-4 max-w-xl">
              <LinkField
                icon={Globe}
                label="WEBSITE"
                value={form.links.website}
                onChange={(v) => update({ links: { ...form.links, website: v } })}
                placeholder="https://tudominio.com"
              />
              <LinkField
                icon={InstagramLogo}
                label="INSTAGRAM"
                value={form.links.instagram}
                onChange={(v) => update({ links: { ...form.links, instagram: v } })}
                placeholder="@tu_handle"
              />
              <LinkField
                icon={SpotifyLogo}
                label="SPOTIFY"
                value={form.links.spotify}
                onChange={(v) => update({ links: { ...form.links, spotify: v } })}
                placeholder="Artist URL"
              />
              <LinkField
                icon={SoundcloudLogo}
                label="SOUNDCLOUD"
                value={form.links.soundcloud}
                onChange={(v) => update({ links: { ...form.links, soundcloud: v } })}
                placeholder="soundcloud.com/tu_handle"
              />
              <LinkField
                icon={YoutubeLogo}
                label="YOUTUBE"
                value={form.links.youtube}
                onChange={(v) => update({ links: { ...form.links, youtube: v } })}
                placeholder="@canal"
              />
            </div>
          )}

          {stage.key === "goals" && (
            <div data-testid="stage-goals" className="space-y-8 max-w-3xl">
              <div>
                <Label>NIVEL DE TRAYECTORIA</Label>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {levels.map((lv) => (
                    <button
                      key={lv.key}
                      data-testid={`level-${lv.key}`}
                      onClick={() => update({ level: lv.key })}
                      className={`text-left p-4 border transition-all ${
                        form.level === lv.key
                          ? "border-[#FF3E00] bg-[#FF3E00]/5"
                          : "border-[#27272A] bg-[#0a0a0a] hover:border-[#F4F4F5]"
                      }`}
                    >
                      <div className="font-display font-bold">{lv.label}</div>
                      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mt-1">
                        {lv.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>
                  ¿QUÉ BUSCAS EN ELARTE/NET?
                  <span className="ml-2 text-[#FF3E00]">
                    {form.interests.length} marcadas
                  </span>
                </Label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {interests.map((i) => {
                    const active = form.interests.includes(i);
                    return (
                      <button
                        key={i}
                        onClick={() => toggleList("interests", i)}
                        className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-colors ${
                          active
                            ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                            : "border-[#27272A] text-[#A1A1AA] hover:text-white"
                        }`}
                      >
                        {active ? "✓ " : "+ "}
                        {i}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {stage.key === "portfolio" && (
            <div data-testid="stage-portfolio" className="space-y-6">
              <PortfolioDrop
                works={form.works}
                onAdd={(w) => update({ works: [...form.works, w] })}
                onRemove={(idx) =>
                  update({ works: form.works.filter((_, i) => i !== idx) })
                }
              />
            </div>
          )}

          {stage.key === "review" && (
            <div data-testid="stage-review" className="max-w-2xl space-y-5">
              <div className="border border-[#27272A] bg-[#0a0a0a] p-5">
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 bg-[#121212] border border-[#27272A] overflow-hidden flex items-center justify-center text-[#525252]">
                    {form.photo ? (
                      <img src={form.photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={24} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] text-[#FF3E00] tracking-wider">
                      @{form.handle || "tu_handle"}
                    </div>
                    <div className="font-display font-black text-3xl tracking-tighter uppercase">
                      {form.name || "Tu nombre"}
                    </div>
                    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                      {form.city || "tu ciudad"} · {form.country}
                    </div>
                  </div>
                </div>
                {form.shortBio ? (
                  <p className="font-display italic text-lg mt-4 text-[#E5E5E5]">
                    “{form.shortBio}”
                  </p>
                ) : null}
              </div>

              <dl className="border border-[#27272A] divide-y divide-[#27272A]">
                <Row k="PERFIL" v={form.type} />
                <Row k="IDIOMAS" v={form.languages.join(" · ")} />
                <Row k="EMAIL" v={form.email || "—"} />
                <Row k="NIVEL" v={form.level} />
                <Row k="INTENCIONES" v={`${form.interests.length} marcadas`} />
                <Row k="OBRAS CARGADAS" v={`${form.works.length} piezas`} />
                <Row
                  k="DISCIPLINAS"
                  v="Las añadirás desde tu perfil"
                />
              </dl>

              <label className="flex items-start gap-3 p-4 border border-[#27272A] bg-[#0a0a0a] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.acceptTerms}
                  onChange={(e) => update({ acceptTerms: e.target.checked })}
                  className="mt-0.5 accent-[#FF3E00]"
                />
                <span className="font-mono text-[11px] text-[#A1A1AA] tracking-wider leading-relaxed">
                  ACEPTO LOS{" "}
                  <a href="#" className="text-[#FF3E00] hover:text-white">
                    TÉRMINOS DE SERVICIO
                  </a>{" "}
                  Y LA{" "}
                  <a href="#" className="text-[#FF3E00] hover:text-white">
                    POLÍTICA DE PRIVACIDAD
                  </a>
                  . CONFIRMO QUE LA OBRA SUBIDA ES DE MI AUTORÍA O TENGO PERMISO
                  PARA USARLA.
                </span>
              </label>
              <label className="flex items-start gap-3 px-4 py-3 border border-[#27272A] bg-[#0a0a0a] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.newsletter}
                  onChange={(e) => update({ newsletter: e.target.checked })}
                  className="mt-0.5 accent-[#FF3E00]"
                />
                <span className="font-mono text-[11px] text-[#A1A1AA] tracking-wider">
                  RECIBIR EL BOLETÍN SEMANAL DEL ECOSISTEMA
                </span>
              </label>

              <div className="border border-[#00FF66] bg-[#00FF66]/5 p-4 flex items-start gap-3">
                <CheckCircle size={16} weight="fill" className="text-[#00FF66] shrink-0 mt-0.5" />
                <div className="font-mono text-[11px] text-[#E5E5E5] tracking-wider leading-relaxed">
                  VERIFICAREMOS TU IDENTIDAD EN 24-48H. MIENTRAS, TU PERFIL ESTÁ
                  PUBLICADO PERO MARCADO COMO "PENDIENTE". SIN COSTO ALGUNO.
                </div>
              </div>
            </div>
          )}

          {/* NAV */}
          <div className="mt-10 flex items-center justify-between gap-4 border-t border-[#27272A] pt-6">
            <button
              disabled={stepIdx === 0}
              onClick={() => setStepIdx((s) => Math.max(0, s - 1))}
              className={`font-mono uppercase text-[10px] tracking-[0.22em] inline-flex items-center gap-2 ${
                stepIdx === 0 ? "text-[#525252]" : "text-[#A1A1AA] hover:text-white"
              }`}
            >
              <CaretLeft size={12} /> Atrás
            </button>
            <div className="flex items-center gap-3">
              {stepIdx < stages.length - 1 ? (
                <button
                  data-testid="register-next"
                  disabled={!canAdvance()}
                  onClick={() => setStepIdx((s) => Math.min(stages.length - 1, s + 1))}
                  className={`btn-brutal font-mono uppercase text-xs tracking-[0.22em] font-bold px-6 py-3 ${
                    canAdvance()
                      ? "bg-[#FF3E00] text-white"
                      : "bg-[#121212] text-[#525252] cursor-not-allowed"
                  }`}
                >
                  Continuar →
                </button>
              ) : (
                <button
                  data-testid="register-submit"
                  disabled={!form.acceptTerms || publishing}
                  onClick={() => setPublishing(true)}
                  className={`btn-brutal font-mono uppercase text-xs tracking-[0.22em] font-bold px-8 py-3 inline-flex items-center gap-2 ${
                    form.acceptTerms && !publishing
                      ? "bg-[#FF3E00] text-white"
                      : "bg-[#121212] text-[#525252] cursor-not-allowed"
                  }`}
                >
                  <SealCheck size={14} weight="fill" /> Crear mi perfil
                </button>
              )}
            </div>
          </div>

          <p className="mt-6 font-mono text-[11px] text-[#A1A1AA] tracking-wider">
            ¿YA TIENES CUENTA?{" "}
            <Link to="/ingresar" className="text-[#FF3E00] hover:text-white">
              INGRESAR
            </Link>
          </p>
        </main>
      </div>
    </div>
  );
}

/* -------- Primitive components -------- */

function Label({ children }) {
  return (
    <label className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] flex items-center">
      // {children}
    </label>
  );
}

function Field({ label, helper, value, onChange, placeholder, type = "text", textarea = false, rows = 3 }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <Label>{label}</Label>
        {helper ? (
          <span className="font-mono text-[10px] text-[#525252] tracking-wider">
            {helper}
          </span>
        ) : null}
      </div>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="mt-2 w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-3 font-display text-[15px] resize-none placeholder:text-[#525252]"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-2 w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-3 font-display text-[15px] placeholder:text-[#525252]"
        />
      )}
    </div>
  );
}

function LinkField({ icon: Icon, label, value, onChange, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 flex items-center border border-[#27272A] focus-within:border-[#FF3E00] bg-[#0a0a0a]">
        <span className="h-12 w-12 inline-flex items-center justify-center border-r border-[#27272A] text-[#A1A1AA]">
          <Icon size={16} weight="regular" />
        </span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent focus:outline-none px-3 font-mono text-sm placeholder:text-[#525252]"
        />
      </div>
    </div>
  );
}

function PhotoUpload({ label, value, onChange, aspect, sub }) {
  const fake = () =>
    onChange(
      "https://images.unsplash.com/photo-1776235522022-68ff83b0b9e0?w=800&q=80"
    );
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {value ? (
          <button
            onClick={() => onChange(null)}
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-[#FF0033] inline-flex items-center gap-1"
          >
            <X size={11} /> Quitar
          </button>
        ) : null}
      </div>
      <button
        onClick={fake}
        className={`mt-2 relative block w-full ${aspect} border-2 border-dashed bg-[#0a0a0a] overflow-hidden transition-colors ${
          value ? "border-[#FF3E00]" : "border-[#27272A] hover:border-[#F4F4F5]"
        }`}
      >
        {value ? (
          <img src={value} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-[#525252] px-4">
            <CloudArrowUp size={28} />
            <div className="font-display font-bold uppercase tracking-tight text-sm text-[#F4F4F5]">
              Subir imagen
            </div>
            <div className="font-mono text-[10px] tracking-wider text-center">{sub}</div>
          </div>
        )}
      </button>
    </div>
  );
}

function PortfolioDrop({ works, onAdd, onRemove }) {
  const seeds = [
    "https://images.unsplash.com/photo-1764345933694-7d4eb49ef266?w=900&q=80",
    "https://images.pexels.com/photos/1774301/pexels-photo-1774301.jpeg",
    "https://images.unsplash.com/photo-1776235522022-68ff83b0b9e0?w=900&q=80",
    "https://images.unsplash.com/photo-1528895709218-bed5456e2c42?w=900&q=80",
    "https://images.pexels.com/photos/3977944/pexels-photo-3977944.jpeg",
  ];
  const fakeUpload = () => {
    if (works.length >= 5) return;
    onAdd({
      id: `w${Date.now()}`,
      title: `Obra ${works.length + 1}`,
      image: seeds[works.length % seeds.length],
    });
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {works.map((w, i) => (
          <div
            key={w.id}
            className="relative aspect-square overflow-hidden border border-[#27272A] group"
          >
            <img src={w.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.22em] bg-black/70 text-white px-1.5 py-0.5">
              {String(i + 1).padStart(2, "0")}
            </div>
            <button
              onClick={() => onRemove(i)}
              className="absolute top-2 right-2 h-6 w-6 bg-[#FF0033] text-white inline-flex items-center justify-center opacity-0 group-hover:opacity-100"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ))}
        {works.length < 5 ? (
          <button
            onClick={fakeUpload}
            data-testid="portfolio-add"
            className="aspect-square border-2 border-dashed border-[#27272A] hover:border-[#FF3E00] bg-[#0a0a0a] flex flex-col items-center justify-center gap-2 text-[#525252] hover:text-[#FF3E00] transition-colors"
          >
            <Plus size={24} />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
              Añadir obra
            </span>
          </button>
        ) : null}
      </div>
      <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider leading-relaxed max-w-xl">
        // SUBE HASTA 5 PIEZAS. ACEPTAMOS IMAGEN, VIDEO O AUDIO · MÁX 50MB POR
        ARCHIVO. PODRÁS AÑADIR MÁS DESPUÉS DESDE TU PERFIL.
      </p>
    </>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 px-4 py-3">
      <dt className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
        {k}
      </dt>
      <dd className="font-display font-semibold text-sm text-[#F4F4F5] truncate">
        {v}
      </dd>
    </div>
  );
}

/* -------- Publishing Overlay (brutalist glitch reveal) -------- */

const PHASES = [
  { t: 400, label: "VERIFICANDO HANDLE" },
  { t: 900, label: "RESERVANDO URL PÚBLICA" },
  { t: 1400, label: "ESCRIBIENDO EN EL DIRECTORIO" },
  { t: 1900, label: "ENLAZANDO COLABORACIONES" },
  { t: 2400, label: "GENERANDO KIT DE PRENSA" },
  { t: 2900, label: "PERFIL PUBLICADO" },
];

function PublishingOverlay({ form, error, onSubmit, onDone, onCancel }) {
  const [elapsed, setElapsed] = useState(0);
  const [phase, setPhase] = useState(0);
  const [apiState, setApiState] = useState("loading"); // loading | ok | error

  // Kick off the real API call once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await onSubmit();
      if (cancelled) return;
      setApiState(ok ? "ok" : "error");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Run the timed animation; navigate only if API responded OK
  useEffect(() => {
    const t0 = performance.now();
    let raf;
    const tick = () => {
      const e = performance.now() - t0;
      setElapsed(e);
      const p = PHASES.findIndex((ph) => e < ph.t);
      setPhase(p === -1 ? PHASES.length - 1 : p);
      if (e < 3300) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // When both animation finished AND api succeeded, navigate
  useEffect(() => {
    if (apiState === "ok" && elapsed >= 3300) {
      const t = setTimeout(onDone, 350);
      return () => clearTimeout(t);
    }
  }, [apiState, elapsed, onDone]);

  const pct = Math.min(100, Math.round((elapsed / 3000) * 100));
  const hash = (seed) =>
    Array.from({ length: 16 })
      .map(() =>
        "0123456789ABCDEF".charAt(
          Math.floor((Math.sin(seed * 9301 + 49297) * 233280) % 16 + 16) % 16
        )
      )
      .join("");

  if (apiState === "error") {
    return (
      <div
        data-testid="publishing-overlay-error"
        className="fixed inset-0 z-[100] bg-[#050505] text-[#F4F4F5] flex items-center justify-center p-6"
      >
        <div className="max-w-md w-full border border-[#FF0033] bg-[#0a0a0a] p-6">
          <div className="font-mono text-[10px] text-[#FF0033] tracking-[0.3em] mb-3">
            // ERROR DE PUBLICACIÓN
          </div>
          <h2 className="font-display text-3xl font-black tracking-tight uppercase mb-3">
            No pudimos crear tu perfil.
          </h2>
          <p className="font-mono text-[12px] text-[#A1A1AA] tracking-wider mb-6">
            {error}
          </p>
          <button
            onClick={onCancel}
            data-testid="publishing-retry"
            className="btn-brutal bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-6 py-3"
          >
            ← Volver a revisar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="publishing-overlay"
      className="fixed inset-0 z-[100] bg-[#050505] text-[#F4F4F5] flex flex-col"
    >
      {/* top bar */}
      <div className="border-b border-[#27272A] px-4 md:px-8 h-16 flex items-center justify-between">
        <div className="font-display text-xl font-black uppercase">
          ELARTE<span className="text-[#FF3E00]">/</span>NET
        </div>
        <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
          TX · {hash(elapsed)}
        </div>
      </div>

      {/* progress rail */}
      <div className="h-0.5 bg-[#27272A]">
        <div
          className="h-full bg-[#FF3E00] transition-[width] duration-100"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* main */}
      <div className="flex-1 grid place-items-center px-6">
        <div className="w-full max-w-[960px] grid grid-cols-1 md:grid-cols-[1fr_360px] gap-10 items-center">
          {/* LEFT — giant text + glitch */}
          <div className="space-y-6">
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.32em]">
              // PUBLICANDO PERFIL · {String(pct).padStart(3, "0")}%
            </div>
            <h1 className="font-display text-[72px] md:text-[120px] leading-[0.82] tracking-tighter font-black uppercase relative">
              <span className="relative inline-block">
                <span
                  className="absolute inset-0 text-[#FF3E00] mix-blend-screen"
                  style={{
                    transform: `translate(${Math.sin(elapsed / 40) * 3}px, 0)`,
                    clipPath: "polygon(0 15%, 100% 15%, 100% 48%, 0 48%)",
                  }}
                  aria-hidden
                >
                  {form.name || "TU NOMBRE"}
                </span>
                <span
                  className="absolute inset-0 text-[#00FF66] mix-blend-screen"
                  style={{
                    transform: `translate(${Math.cos(elapsed / 50) * -3}px, 0)`,
                    clipPath: "polygon(0 60%, 100% 60%, 100% 95%, 0 95%)",
                  }}
                  aria-hidden
                >
                  {form.name || "TU NOMBRE"}
                </span>
                <span className="relative">{form.name || "TU NOMBRE"}</span>
              </span>
            </h1>
            <div className="flex items-baseline gap-4 border-t border-[#27272A] pt-5">
              <span className="font-mono text-[11px] text-[#A1A1AA] tracking-[0.22em]">
                ELARTE.NET/ARTISTA/
              </span>
              <span className="font-display font-bold text-2xl md:text-3xl text-[#FF3E00] tracking-tight truncate">
                @{form.handle || "tu_handle"}
              </span>
            </div>
          </div>

          {/* RIGHT — log ticker */}
          <div className="border border-[#27272A] bg-[#0a0a0a] p-5">
            <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-4">
              // REGISTRO EN TIEMPO REAL
            </div>
            <ul className="space-y-2">
              {PHASES.map((p, i) => {
                const state =
                  i < phase ? "done" : i === phase ? "active" : "queued";
                return (
                  <li
                    key={p.label}
                    className="flex items-center gap-3 font-mono text-[11px] tracking-[0.14em]"
                  >
                    <span
                      className={`h-1.5 w-1.5 ${
                        state === "done"
                          ? "bg-[#00FF66]"
                          : state === "active"
                          ? "bg-[#FF3E00] animate-pulse"
                          : "bg-[#27272A]"
                      }`}
                    />
                    <span
                      className={
                        state === "done"
                          ? "text-[#00FF66]"
                          : state === "active"
                          ? "text-[#F4F4F5]"
                          : "text-[#525252]"
                      }
                    >
                      {state === "done" ? "OK" : state === "active" ? "··" : "—"}
                    </span>
                    <span
                      className={
                        state === "done"
                          ? "text-[#A1A1AA]"
                          : state === "active"
                          ? "text-[#F4F4F5]"
                          : "text-[#525252]"
                      }
                    >
                      {p.label}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-5 pt-4 border-t border-[#27272A] font-mono text-[10px] text-[#525252] tracking-[0.22em]">
              NODE · MAD-03 · BLOCK #{String(Math.floor(elapsed)).padStart(5, "0")}
            </div>
          </div>
        </div>
      </div>

      {/* bottom ticker */}
      <div className="border-t border-[#27272A] overflow-hidden whitespace-nowrap">
        <div
          className="inline-block font-mono text-[10px] tracking-[0.32em] text-[#525252] py-3"
          style={{ transform: `translateX(-${(elapsed / 20) % 600}px)` }}
        >
          {Array.from({ length: 8 })
            .map(
              () =>
                "··· SIN BOTS ··· SIN PORTFOLIOS FALSOS ··· VERIFICACIÓN MANUAL 24-48H ··· "
            )
            .join(" ")}
        </div>
      </div>
    </div>
  );
}
